import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { FeatureGate } from '@/components/ui/FeatureGate';
import { CategoryCriteriaMatrix } from '@/features/analysis-setup/components/CategoryCriteriaMatrix';
import {
  validateCategoriesSearch,
  type CategoriesTab,
} from '@/features/analysis-setup/categories-search';
import {
  ProfilesWorkbench,
  StageBindingsTab,
} from '@/features/prompt-builder/components';

export const Route = createFileRoute('/analysis-setup/categories')({
  validateSearch: validateCategoriesSearch,
  component: () => (
    <FeatureGate flag="enablePromptBuilder">
      <AnalysisSetupCategoriesStep />
    </FeatureGate>
  ),
});

// Same gate as the old /prompt-builder page (Task 6 folds it into this step).
// Categories (the new binding matrix) is the default tab; profiles/stages are
// the same tab bodies /prompt-builder used, unmodified.
function AnalysisSetupCategoriesStep() {
  const { t } = useTranslation('analysisSetup');
  const navigate = useNavigate();
  const { profileId, tab } = Route.useSearch();
  const activeTab: CategoriesTab = tab ?? 'categories';
  // The stages tab reads the CRM live: mount it on its first visit, then keep it.
  const [stagesOpened, setStagesOpened] = useState(activeTab === 'stages');
  if (activeTab === 'stages' && !stagesOpened) setStagesOpened(true);

  return (
    <Tabs
      value={activeTab}
      onValueChange={(v) => {
        const next = (Array.isArray(v) ? v[0] : v) as CategoriesTab;
        void navigate({
          to: '/analysis-setup/categories',
          search: { profileId, tab: next === 'categories' ? undefined : next },
          replace: true,
        });
      }}
    >
      <TabsList variant="underline" className="mb-4 px-0">
        <TabsTrigger
          value="categories"
          variant="underline"
          data-testid="criteria-tab-categories"
        >
          {t('tabs.categories')}
        </TabsTrigger>
        <TabsTrigger
          value="profiles"
          variant="underline"
          data-testid="criteria-tab-profiles"
        >
          {t('tabs.profiles')}
        </TabsTrigger>
        <TabsTrigger value="stages" variant="underline" data-testid="criteria-tab-stages">
          {t('tabs.stages')}
        </TabsTrigger>
      </TabsList>

      <TabsContent value="categories" className="mt-0">
        <CategoryCriteriaMatrix />
      </TabsContent>

      {/* forceMount: in-progress profile edits (toggle state, transcript,
          analysis result) must survive a visit to the categories/stages tabs
          — copied from prompt-builder.tsx's profiles TabsContent.
          The workbench is three panels that scroll inside themselves, so it
          needs a height of its own: the page scrolls, and without one the
          panels would stretch to their content. */}
      <TabsContent
        value="profiles"
        forceMount
        className="mt-0 h-[calc(100dvh-20rem)] min-h-[32rem] flex-col overflow-hidden rounded-card border border-border data-[state=active]:flex"
      >
        <ProfilesWorkbench initialProfileId={profileId} />
      </TabsContent>

      {/* forceMount once opened: a visit that never opens the stages tab
          must not read the CRM, and one that did keeps it. TabsContent hides
          inactive content itself (`data-[state=inactive]:hidden`); this one is
          not flex, so nothing else is needed. */}
      <TabsContent value="stages" forceMount={stagesOpened || undefined} className="mt-0">
        <StageBindingsTab />
      </TabsContent>
    </Tabs>
  );
}
